import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Heading } from "../../design/heading";
import { Text } from "../../design/text";
import { Badge } from "../../design/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../design/table";
import { Visit, VisitStatus } from "@visitproof/types";
import {
  collection,
  query,
  where,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../../firebase";
import moment from "moment";
import {
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
} from "@heroicons/react/20/solid";

const getStatusBadge = (status: VisitStatus) => {
  switch (status) {
    case "scheduled":
      return <Badge color="blue">Scheduled</Badge>;
    case "supervisor_enroute":
      return <Badge color="yellow">En Route</Badge>;
    case "supervisor_arrived":
      return <Badge color="orange">Arrived</Badge>;
    case "supervisor_started":
      return <Badge color="green">In Progress</Badge>;
    case "supervisor_ended":
    case "completed":
      return <Badge color="zinc">Completed</Badge>;
    case "canceled":
      return <Badge color="red">Canceled</Badge>;
    case "no_show":
      return <Badge color="red">No Show</Badge>;
    default:
      return <Badge color="zinc">{status}</Badge>;
  }
};

const VisitCard: React.FC<{ visit: Visit }> = ({ visit }) => {
  const startTime = moment(visit.startDate.toDate());
  const endTime = moment(visit.endDate.toDate());

  return (
    <Link
      to={`/visit/${visit.id}`}
      className="block p-4 transition-colors border rounded-lg border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <CalendarIcon className="w-4 h-4 text-zinc-500" />
            <Text className="font-medium">
              {startTime.format("dddd, MMMM D, YYYY")}
            </Text>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <ClockIcon className="w-4 h-4 text-zinc-500" />
            <Text>
              {startTime.format("h:mm A")} - {endTime.format("h:mm A")}
            </Text>
          </div>
          {visit.location && (
            <div className="flex items-center gap-2">
              <MapPinIcon className="w-4 h-4 text-zinc-500" />
              <Text className="text-zinc-600 dark:text-zinc-400">
                {visit.location.name || visit.location.formattedAddress}
              </Text>
            </div>
          )}
        </div>
        <div>{getStatusBadge(visit.status)}</div>
      </div>
    </Link>
  );
};

type TabType = "upcoming" | "past" | "all";

/**
 * Parent visits page - shows visits they're associated with
 */
const ParentVisits: React.FC = () => {
  const { claims, loading: authLoading } = useAuth();
  const [visits, setVisits] = useState<Visit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>("upcoming");

  const accountId = claims?.accountId || null;
  const parentId = accountId ? claims?.accounts?.[accountId]?.parentId || null : null;

  // Load visits
  useEffect(() => {
    // Parents only need parentId to query visits (no accountId required)
    if (authLoading || !parentId) {
      if (!authLoading) {
        setVisits([]);
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    console.log("ParentVisits: Loading visits for parentId:", parentId);

    // Query visits where parentId is in parentIds array
    // Security rules allow this: allow read: if isParent() && getUserParentId() in resource.data.parentIds
    const visitsQuery = query(
      collection(db, "visits"),
      where("parentIds", "array-contains", parentId)
    );

    const unsubscribe = onSnapshot(
      visitsQuery,
      (snapshot) => {
        const visitList = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as Visit[];
        // Sort by date
        visitList.sort((a, b) => {
          const dateA = a.startDate?.toDate?.() || new Date(0);
          const dateB = b.startDate?.toDate?.() || new Date(0);
          return dateA.getTime() - dateB.getTime();
        });
        setVisits(visitList);
        setLoading(false);
      },
      (err) => {
        console.error("Error loading visits:", err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [authLoading, parentId]);

  // Computed visit lists
  const { upcomingVisits, pastVisits } = useMemo(() => {
    const now = new Date();

    const upcoming = visits.filter((visit) => {
      const visitDate = visit.startDate?.toDate?.();
      if (!visitDate) return false;
      return visitDate > now && visit.status !== "canceled";
    });

    const past = visits.filter((visit) => {
      const visitDate = visit.endDate?.toDate?.();
      if (!visitDate) return false;
      return visitDate < now;
    }).reverse();

    return { upcomingVisits: upcoming, pastVisits: past };
  }, [visits]);

  const getVisitsForTab = () => {
    switch (activeTab) {
      case "upcoming":
        return upcomingVisits;
      case "past":
        return pastVisits;
      case "all":
        return visits;
      default:
        return visits;
    }
  };

  const displayedVisits = getVisitsForTab();

  if (loading || authLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Text>Loading...</Text>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20">
        <Text className="text-red-600 dark:text-red-400">{error}</Text>
      </div>
    );
  }

  const tabs: { key: TabType; label: string; count: number }[] = [
    { key: "upcoming", label: "Upcoming", count: upcomingVisits.length },
    { key: "past", label: "Past", count: pastVisits.length },
    { key: "all", label: "All", count: visits.length },
  ];

  return (
    <div className="p-6">
      <div className="mb-6">
        <Heading>My Visits</Heading>
        <Text className="mt-2 text-zinc-600 dark:text-zinc-400">
          View your scheduled and past visits
        </Text>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 mb-6 rounded-lg bg-zinc-100 dark:bg-zinc-800 w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === tab.key
                ? "bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white shadow-sm"
                : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white"
            }`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Visit Cards for Upcoming */}
      {activeTab === "upcoming" && displayedVisits.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2">
          {displayedVisits.map((visit) => (
            <VisitCard key={visit.id} visit={visit} />
          ))}
        </div>
      ) : displayedVisits.length === 0 ? (
        <div className="p-8 text-center border rounded-lg border-zinc-200 dark:border-zinc-700">
          <Text className="text-zinc-500">
            {activeTab === "upcoming"
              ? "No upcoming visits scheduled"
              : activeTab === "past"
              ? "No past visits"
              : "No visits found"}
          </Text>
        </div>
      ) : (
        /* Table for Past/All */
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Date</TableHeader>
              <TableHeader>Time</TableHeader>
              <TableHeader>Location</TableHeader>
              <TableHeader>Status</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedVisits.map((visit) => {
              const startTime = moment(visit.startDate.toDate());
              const endTime = moment(visit.endDate.toDate());
              return (
                <TableRow key={visit.id} href={`/visit/${visit.id}`}>
                  <TableCell className="font-medium">
                    {startTime.format("ddd, MMM D, YYYY")}
                  </TableCell>
                  <TableCell>
                    {startTime.format("h:mm A")} - {endTime.format("h:mm A")}
                  </TableCell>
                  <TableCell>
                    {visit.location?.name || visit.location?.formattedAddress || "—"}
                  </TableCell>
                  <TableCell>{getStatusBadge(visit.status)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export default ParentVisits;
